import config from 'src/config';

import Model, { storagePrefix } from 'src/storage/base';
import { popupModel } from './components/complex';
import AlertManager from './components/alert/AlertManager';
import { lang, permissions, upperFirstCharAll, getRouter, device, hasSameId } from './helpers';
import DoctorSignupPopup from 'src/pages/auth/SignupDoctor/Popup';
import ClinicSignupPopup from 'src/pages/auth/SignupClinic/Popup';
import VerifyEmailPopup from 'src/pages/auth/Verification/Popup';
import AddLocationPopup from 'src/components/location/AddLocation';
import PaymentResultPopup from 'src/components/paymentCard/PaymentResultPopup';
import locationsModel from 'src/components/location/model';
import LANG_CONST from 'src/helpers/lang/const';
import constants from './constants';

// Tokens are stored under their own keys, NOT inside the persisted app state.
// They use the shared storagePrefix, so BaseStore.clearLocalStorage() also
// removes them on logout.
const SID_KEY = storagePrefix + 'app_sid';
// sid of the previous account (used by "login as" / back to previous account)
const PREV_SID_KEY = storagePrefix + 'app_prev_sid';

const saveKey = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore (storage blocked / full)
  }
};
const loadKey = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const clearKey = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

// Remove tokens from a user/profile object (returns a new object).
// - user.sid is dropped (the main sid is stored in SID_KEY)
// - user.loginInfo.sid is moved to PREV_SID_KEY
const cleanUser = (user) => {
  if (!user || typeof user !== 'object') return user;
  // eslint-disable-next-line no-unused-vars
  const { sid: _sid, loginInfo, ...rest } = user;
  if (loginInfo && typeof loginInfo === 'object') {
    const { sid: prevSid, ...info } = loginInfo;
    if (prevSid) saveKey(PREV_SID_KEY, prevSid);
    rest.loginInfo = info;
  }
  return rest;
};

// Take an auth response (or state), save tokens to their own keys,
// and return a copy that is safe to put into persisted state.
const extractTokens = (data) => {
  const { sid, ...rest } = data;
  if (sid) {
    config.set('sid', sid);
    saveKey(SID_KEY, sid);
  }
  if (rest.user) rest.user = cleanUser(rest.user);
  // profile and user are always the same object in handleProfile
  if (rest.profile) rest.profile = rest.user || cleanUser(rest.profile);
  return rest;
};

class AppModel extends Model {
  id = 'app';
  persistent = true;

  defaultState = () => ({
    clinicLocationsCheckedUserId: null
  });

  apiConfig = {
    profile: {
      url: 'auth/sid',
      validation: {
        sid: 'string'
      },
      cancellable: true
    },
    logout: {
      url: 'auth/logout',
      validation: {
        sid: 'string'
      },
      cancellable: true
    },
    location: {
      url: 'country/ip',
      validation: {},
      cancellable: true
    },
    switchAccount: {
      url: 'auth/switchConnectedAccount',
      validation: {
        id: {
          type: 'number',
          required: true
        }
      }
    },
    removeAccount: {
      url: 'auth/remove_account',
      validation: {},
      cancellable: false
    },
    saveAccountState: {
      url: 'profile/set/accountState',
      validation: {},
      cancellable: false
    },
    emailVerificationRequestedAt: {
      url: 'profile/set/emailVerificationRequestedAt',
      validation: {},
      cancellable: false
    },
    getUnreadCounters: 'contact/getUnreadCounters',
    clinicLocations: 'clinic/manager/hospitals'
  };
  _events = {
    auth: 'handleAuth',
    authSilent: 'handleAuthSilent',
    logout: 'handleLogout',
    profile: 'handleProfile',
    'foxsocket:message:profile_update': 'handleProfileUpdateWS',
    'foxsocket:message:notification': 'handleNotification',
    resetNotifications: 'handleResetNotifications',
    switchAccount: 'switchAccount',

    'foxsocket:message:unreadcounter_change': 'onChangeUnreadCounterWS',
    unreadcounter_change: 'onChangeUnreadCounter',
    activeMessageContact: 'handleActiveMessageContact',
    showEmailPopup: 'showEmailPopup',
    showSignupPopup: 'showSignupPopup',
    deleteAccount: 'deleteAccount',
    'foxsocket:message:logout_all': 'WSHandleLogoutAll',
    'foxsocket:message:logout_sid': 'WSHandleLogoutSid',
    checkDisabledAccount: 'checkDisabledAccount',

    payment_result: 'showPaymentResultPopup'
  };

  WSHandleLogoutSid = ({ data }) => {
    let { sid } = data || {};
    // read from config, do not log sid values
    let currentSid = config.get('sid');
    this.log('WSHandleLogoutSid');
    if (sid && currentSid === sid) {
      this.log('WSHandleLogoutSid: logout');
      this.emit('logout');
    }
  };

  WSHandleLogoutAll = ({ data }) => {
    let { except } = data || {};
    // read from config, do not log sid values
    let sid = config.get('sid');
    this.log('WSHandleLogoutAll');
    if (sid && sid !== except) {
      this.log('WSHandleLogoutAll: logout');
      this.emit('logout');
    }
  };

  isLoggedIn = () => {
    return !!this.state.user;
  };

  isAdmin = () => {
    return !!this.state.user?.isAdmin;
  };

  async start(cb) {
    try {
      await this.load();

      // Migration: move old tokens from persisted state to their own keys
      let dirty = false;

      if (this.state.sid) {
        if (!loadKey(SID_KEY)) saveKey(SID_KEY, this.state.sid);
        delete this.state.sid;
        dirty = true;
      }

      if (this.state.tempLoginInfo?.sid) {
        const { sid: prevSid, ...info } = this.state.tempLoginInfo;
        if (!loadKey(PREV_SID_KEY)) saveKey(PREV_SID_KEY, prevSid);
        this.state.tempLoginInfo = info;
        dirty = true;
      }

      const hasUserToken = (u) => !!u && (!!u.sid || !!u.loginInfo?.sid);
      if (hasUserToken(this.state.user) || hasUserToken(this.state.profile)) {
        if (this.state.user) this.state.user = cleanUser(this.state.user);
        if (this.state.profile) this.state.profile = cleanUser(this.state.profile);
        dirty = true;
      }

      if (dirty) this.saveState(true); // silent save, removes tokens from persisted state

      if (this.state.appSettings)
        config.set('appSettings', {
          ...config.get('appSettings'),
          ...this.state.appSettings
        });

      // read sid from its own key, and set config early
      let sid = loadKey(SID_KEY);
      if (sid) config.set('sid', sid);

      if (sid) {
        if (this.state.user) {
          this.handleAuth(this.state, false);
          cb(true);
        }
        this.api(
          'profile',
          { sid, username: this.user?.username || '', platform: 'web' },
          (error, data, success) => {
            if (success) {
              this.handleAuth(data);
              cb(true);
            } else {
              cb(false);
              this.emit('logout');
            }
          }
        );
      } else {
        cb(false);
        if (this.isLogged) {
          this.emit('logout');
        }
      }
    } catch (e) {
      console.warn(e);
      cb(false);
    }
    this.loadLocation();
  }

  loadLocation = () => {
    this.api('location', {}, (err, data, success) => {
      if (success) {
        lang.setLocation(data);
        this.emit('location', data);
      }
    });
  };

  handleAuth = (data, showSignPopup = true) => {
    this.log('handleAuth');
    // strip all tokens so they never go into persisted state
    data = extractTokens(data);

    let profile = data.user;
    if (profile && !profile?.connectedAccounts) {
      profile.connectedAccounts = [
        {
          id: profile.id,
          role: profile.isDoctor
            ? 'doctor'
            : profile.isClinic
              ? 'clinic'
              : profile.isManager
                ? 'manager'
                : 'patient'
        }
      ];
    }
    data.timeshift = Date.now() - data.date;

    if (!this.emitLogged) {
      this.emitLogged = true;
      this.emit('logged', data.user);
    }
    this.emit('profile', profile);

    data.tempLoginInfo = null;
    let isTemporary = profile.loginInfo && profile.loginInfo.type === 'temporary';
    if (isTemporary) data.tempLoginInfo = { ...profile.loginInfo };

    this.setState(data);

    if (showSignPopup) this.checkDisabledAccount(profile);
    this.handleAuthPendingPage();
    this.loadUnreadCounters();
    if (!isTemporary) {
      this.notification.requestPermission(profile);
    }
    if (device === 'ios' || device === 'android') {
      return;
    }

    if (showSignPopup) {
      this.showSignupPopup();
      this.showClinicLocationsPopup();
    }
  };

  isDoctorProfileComplete = () => {
    let {
      specialist,
      description,
      charge,
      doctorType,
      yearOfStarting,
      institution,
      certificateFiles
    } = this.state.user || {};
    // return false;
    try {
      return (
        specialist?.length > 0 &&
        description?.length > 0 &&
        // treatment step is removed in doctor's signup popup
        // treatments?.length > 0 &&
        charge &&
        charge.first &&
        charge.first.virtual !== null &&
        doctorType &&
        yearOfStarting &&
        institution &&
        certificateFiles?.length > 0
        // false
      );
    } catch {
      return false;
    }
  };

  isNotAuthPage = () => {
    return (
      (!permissions.history || permissions?.history?.url?.toString().indexOf('signup') === -1) &&
      window.location.href.indexOf('signup') === -1 &&
      window.location.href.indexOf('phone_verification') === -1 &&
      window.location.href.indexOf('email_verification') === -1 &&
      window.location.href.indexOf('account_removal') === -1 &&
      window.location.href.indexOf('signup') === -1
    );
  };

  isNotAdminPage = () => {
    return window.location.href.indexOf('/admin/') === -1;
  };

  showSignupPopup = () => {
    let isDoctorProfileComplete = this.isDoctorProfileComplete();
    console.warn('showSignupPopup', {
      isDoctorProfileComplete,
      isDoctor: this.state.user?.isDoctor,
      isNotAuthPage: this.isNotAuthPage(),
      isNotAdminPage: this.isNotAdminPage(),
      user: this.state.user,
      result:
        this.state.user?.isDoctor &&
        !isDoctorProfileComplete &&
        this.isNotAuthPage() &&
        this.isNotAdminPage()
    });
    if (
      this.state.user?.isDoctor &&
      !isDoctorProfileComplete &&
      this.isNotAuthPage() &&
      this.isNotAdminPage() &&
      window.location.href.indexOf('/settings/certificate') === -1
    ) {
      popupModel.setActivePopup('DoctorSignupPopup', {
        requireAuth: true,
        // active: true, using activeByRouter to active auto
        component: DoctorSignupPopup,
        showAlone: true,
        activeByRouter: ({ location }) => {
          const match = permissions.findMatchPath(location.pathname);

          if (!match) return true;
          return match.webType === 'front' && !match.isAuthPage;
        }
      });
    } else if (this.state.user?.isClinic && !this.state.user?.specialist?.length) {
      popupModel.setActivePopup('ClinicSignupPopup', {
        requireAuth: true,
        // active: true, using activeByRouter to active auto
        component: ClinicSignupPopup,
        showAlone: true,
        activeByRouter: ({ location }) => {
          const match = permissions.findMatchPath(location.pathname);

          if (!match) return true;
          return match.webType === 'front' && !match.isAuthPage;
        }
      });
    } else {
      this.emit('showEmailPopup');
    }
  };

  showEmailPopup = () => {
    popupModel.setActivePopup('EmailVerify', {
      requireAuth: true,
      // active: true, using activeByRouter to active auto
      showAlone: true,
      component: VerifyEmailPopup,
      afterHide: () => {
        const user = this.state.user;
        user.emailVerificationRequestedAt = Date.now();
        this.api('emailVerificationRequestedAt', { value: Date.now() });
      },
      activeByRouter: ({ location }) => {
        const user = this.state.user;
        const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;

        if (
          !user ||
          user?.emailVerified ||
          (!!user?.emailVerificationRequestedAt &&
            Date.now() - user?.emailVerificationRequestedAt < oneWeekInMs) //Less than a week has passed.
        ) {
          popupModel.getActivePopup('EmailVerify').activeByRouter = null;
          return false;
        }
        const match = permissions.findMatchPath(location.pathname);

        if (!match) return true;
        return match.webType === 'front' && !match.isAuthPage;
      }
    });
  };

  showClinicLocationsPopup = () => {
    const clinicUserId = this.state.user?.id;

    if (
      this.state.user?.isClinic &&
      this.state.clinicLocationsCheckedUserId !== clinicUserId &&
      this.isNotAuthPage() &&
      this.isNotAdminPage()
    ) {
      this.setStateSilent({ clinicLocationsCheckedUserId: clinicUserId });
      this.api('clinicLocations', { skip: 0, limit: 1 }, (err, data, success) => {
        console.warn('showClinicLocationsPopup', success, data);

        if (!success) {
          this.setStateSilent({ clinicLocationsCheckedUserId: null });
          return;
        }

        if (success && data?.items?.length === 0) {
          popupModel.setActivePopup('add_location', {
            requireAuth: true,
            // active: true, using activeByRouter to active auto
            showAlone: true,
            component: AddLocationPopup,
            props: { model: locationsModel, addManager: !this.state.user?.isDoctor },
            activeByRouter: ({ location }) => {
              const match = permissions.findMatchPath(location.pathname);

              if (!match) return true;
              return match.webType === 'front' && !match.isAuthPage;
            }
          });
        }
      });
    }
  };

  showPaymentResultPopup = (data) => {
    let res = data.data;
    popupModel.setActivePopup('PaymentResultPopup', {
      requireAuth: false,
      active: true,
      component: PaymentResultPopup,
      afterHide: () => {
        if (res?.chargeResult?.success && res?.invoice) getRouter().navigate('/invoices');
      },
      props: {
        response: data
      }
    });
  };

  handleAuthPendingPage = () => {
    if (this.state.authPendingPage) {
      let { route, event } = this.state.authPendingPage;
      if (event && event.name) {
        this.emit(event.name, event.params);
      }
      if (route) {
        getRouter().navigate(route);
      }
      this.setStateSilent({ authPendingPage: null });
    }
  };

  handleAuthSilent = (data) => {
    // strip all tokens, only write when present
    const rest = extractTokens(data);
    rest.timeshift = Date.now() - rest.date;

    if (!this.emitLogged) {
      this.emitLogged = true;
    }
    this.setStateSilent(rest);
  };

  handleLogout = (params) => {
    window.sessionStorage.clear();
    this.emitLogged = false;
    let sid = config.get('sid');
    if (!(params && params.noApi)) {
      this.api('logout', {
        sid
      });
    }
    config.set('sid', false);
    clearKey(SID_KEY); // remove stored sid
    clearKey(PREV_SID_KEY); // remove stored previous-account sid
    this.trigger();
  };

  handleProfile = (profile) => {
    this.foxsocket().subscribe('g-general');
    this.setState({ profile, user: profile }, null, true);
  };

  handleProfileUpdateWS = ({ data }) => {
    if (data.blocked) {
      this.emit('logout');
    }
    let profile = this.state.profile || {};
    const find = data?.connectedAccounts?.find((u) => u.id === profile.id);
    if (find) {
      profile.status = find.status;
    }
    let statusChanged = data.status && profile.status !== data.status;
    let managerAdminChanged =
      profile.isManager &&
      data.managerAdmin !== undefined &&
      data.managerAdmin !== profile.managerAdmin;
    let managerAccessChanged =
      profile.isManager && !!data.managerAccess && data.managerAccess !== profile.managerAccess;

    if (data.isRemoved) {
      return this.emit('logout');
    }

    if (data.connectedAccounts || data.status) {
      this.checkDisabledAccount(data);
    }

    Object.keys(data).map((key) => (profile[key] = data[key]));

    if (managerAdminChanged || managerAccessChanged || statusChanged) {
      const params = {
        auth: profile,
        logged: true,
        isAdmin: this.isAdmin()
      };
      permissions.run(params);
      this.warn('emit rerender', { statusChanged }, managerAdminChanged, managerAccessChanged);
      this.emit('rerender');
    }

    this.emit('profile', profile);
  };

  checkDisabledAccount = (data) => {
    let profile = this.state.profile || {};
    const find = (
      data.connectedAccounts ||
      profile.connectedAccounts || [{ id: profile.id, role: profile.role, status: profile.status }]
    )?.find((u) => u.id === profile.id);

    let statusChanged = data.status || (find && find.status);

    if (statusChanged && statusChanged === constants.general.userStatus.disabled) {
      AlertManager.popup(AlertManager.TYPES.INFO, {
        maxWidth: 470,
        close: false,
        title:
          find.role?.includes('manager') || find.role?.includes('doctor_')
            ? lang.t(LANG_CONST.notice.clinic_disabled_account, {
                clinic: upperFirstCharAll(find?.clinicName)
              })
            : lang.t(LANG_CONST.notice.disabled_account),

        actionButton: {
          label: LANG_CONST.buttons.ok,
          action: (props) => props.hide()
        }
      });
      return false;
    }

    return true;
  };

  handleNotification = () => {
    let profile = this.state.profile || {};
    if (!profile.unreadNotifications) profile.unreadNotifications = 0;
    profile.unreadNotifications += 1;
    this.emit('profile', profile);
  };

  handleResetNotifications = () => {
    let profile = this.state.profile || {};
    profile.unreadNotifications = 0;
    this.emit('profile', profile);
  };

  getTimeShift = () => {
    return this.state?.timeshift || 0;
  };

  checkConfirmCookiePolicy = async () => {
    let value = await this.getCookiePolicy();
    if (value !== '1') {
      popupModel.openCookiePolicy({
        ok: () => {
          value = '1';
          this.setCookiePolicy(value).then();
        },
        cancel: () => {
          value = '0';
          this.setCookiePolicy(value).then();
        }
      });
    }
    return value === '1';
  };

  switchAccount = (params, cb) => {
    let id = params && params.id ? params.id : params;
    if (id == this.state.user?.id) return this.log('switchAccount: same account, ignore', params);
    let partnerId = params && params.partnerId ? '/' + params.partnerId : null;
    if (this.getApiState('switchAccount').loading)
      return this.log('switchAccount: already switching', params);
    this.api('switchAccount', { id }, (err, res, success) => {
      if (success) {
        this.emit('resetStates');
        this.emit('auth', res);
        this.foxsocket().disconnect();
        this.foxsocket().ensureConnect(true);
        let route = '';
        if (partnerId) {
          route = 'messages/' + partnerId;
        }
        getRouter().navigate('/' + route);
        this.emit('accountSwitched', res);
      }
      setTimeout(() => {
        typeof cb === 'function' && cb(err, res, success);
      }, 1);
    });
  };

  // NOTE: signature changed. The previous-account sid is read from its own
  // storage key, no longer passed in from state.
  backPreviousAccount = (user, navigate, cb) => {
    if (this.getApiState('profile').loading)
      return this.log('backPreviousAccount: switching', { user });

    const sid = loadKey(PREV_SID_KEY);
    if (!sid) {
      this.log('backPreviousAccount: no previous session');
      cb && cb('no previous session', null, false);
      return;
    }

    this.api('profile', { sid, username: user?.username }, (err, res, success) => {
      if (success) {
        clearKey(PREV_SID_KEY);
        this.emit('resetStates');
        this.emit('auth', res);
        this.foxsocket().disconnect();
        this.foxsocket().ensureConnect(true);
        navigate && getRouter().navigate(navigate.url, navigate.params);
        this.emit('accountSwitched', res);
      }
      cb && cb(err, res, success);
    });
  };

  deleteAccount = () => {
    AlertManager.popup(AlertManager.TYPES.WARN, {
      close: false,
      title: LANG_CONST.notice.delete_account,
      cancelButton: {
        label: LANG_CONST.buttons.no,
        action: (actions) => actions.hide()
      },
      actionButton: {
        label: LANG_CONST.buttons.yes,
        loadingLabel: LANG_CONST.buttons.processing,
        action: (actions) => {
          actions.setLoading(true);
          this.removeAccount((err, res, success) => {
            actions.setLoading(false);
            if (success) {
              actions.hide();
            } else {
              alert(`error: ${err}`);
            }
          });
        }
      }
    });
  };

  removeAccount = (cb) => {
    this.api('removeAccount', {}, (err, res, success) => {
      if (success) {
        this.emit('logout', { noApi: true });
      }
      cb && cb(err, res, success);
    });
  };

  loadUnreadCounters = () => {
    this.api('getUnreadCounters', {}, (err, res, success) => {
      this.setState({ unreadCounters: (success && res) || [] });
    });
  };

  handleActiveMessageContact = (data = {}) => {
    this.activeMessageContactIds = (data?.ids || [data?.partnerId]).filter(
      (id) => id !== null && id !== undefined && id !== ''
    );
  };

  isMessageContactActive = (partnerId) => {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const onMessagesRoute = /^\/(admin\/)?messages(\/|$)/.test(pathname);

    return (
      onMessagesRoute &&
      (this.activeMessageContactIds || []).some((activeId) => hasSameId(activeId, partnerId))
    );
  };

  getUnreadCounterDeltaKey = ({ userId, partnerId }) => `${userId}:${partnerId}`;

  getPendingActiveUnreadCounterDelta = (key) => {
    const pending = this.pendingActiveUnreadCounterDeltas?.[key];

    if (!pending) return null;
    if (Date.now() - pending.date > 15000) {
      delete this.pendingActiveUnreadCounterDeltas[key];
      return null;
    }

    return pending;
  };

  onChangeUnreadCounterWS = (event = {}) => {
    const payload = event?.data || event;

    this.log('onChangeUnreadCounterWS', payload);
    let { userId, value, partnerId } = payload;
    if (this.shouldIgnoreUnreadCounterDelta({ userId, partnerId, value })) return;
    this.updateUnreadCounter(userId, value);
  };

  onChangeUnreadCounter = ({ userId, value, partnerId }) => {
    this.log('onChangeUnreadCounter', { userId, value, partnerId });
    if (this.shouldIgnoreUnreadCounterDelta({ userId, partnerId, value })) return;
    this.updateUnreadCounter(userId, value);
  };

  shouldIgnoreUnreadCounterDelta = ({ userId, partnerId, value }) => {
    if (!partnerId) return false;
    if (!hasSameId(userId, this.userId) && !hasSameId(userId, this.state.user?.id)) return false;
    if (hasSameId(userId, partnerId)) return true;

    let delta = Number(value) || 0;
    let key = this.getUnreadCounterDeltaKey({ userId, partnerId });
    let pending = this.getPendingActiveUnreadCounterDelta(key);
    this.pendingActiveUnreadCounterDeltas = this.pendingActiveUnreadCounterDeltas || {};

    if (delta > 0 && this.isMessageContactActive(partnerId)) {
      this.pendingActiveUnreadCounterDeltas[key] = {
        count: (pending?.count || 0) + 1,
        date: Date.now()
      };
      return true;
    }

    if (delta < 0 && pending?.count > 0) {
      pending.count -= 1;
      if (pending.count <= 0) delete this.pendingActiveUnreadCounterDeltas[key];
      return true;
    }

    return false;
  };

  updateUnreadCounter = (userId, value) => {
    let delta = Number(value) || 0;
    let found = false;
    let unreadCounters = [...(this.state.unreadCounters || [])].map((counter) => {
      if (!hasSameId(counter.id, userId)) return counter;

      found = true;
      return {
        ...counter,
        unread: Math.max(0, (Number(counter.unread) || 0) + delta)
      };
    });

    if (!found && delta > 0) {
      unreadCounters.push({ id: userId, unread: delta });
    }

    this.setState({ unreadCounters });
    return unreadCounters;
  };

  saveAccountState = (accountState) => {
    if (accountState?.state !== this.state.user?.accountState)
      this.api('saveAccountState', { accountState }, (err) => {
        if (!err) this.setState({ user: { ...this.state.user, accountState } });
      });
  };
}

const app = new AppModel();
export default app;
